import { motion } from "framer-motion";
import { ArrowUpRight, MoveRight } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/4e1ae213-eba7-4c3b-9a90-84915d76d4df/images/30619d7ce1ea8ae9dffc59b6a68b4179df1ec690f82c6af7cd53f7b9806c818c.png";

export default function Hero() {
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0E2750]"
    >
      {/* Background image — refined, calm */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="Architectural detail, evening light"
          className="h-full w-full object-cover opacity-30"
          loading="eager"
        />
        {/* deep navy wash to keep the image quiet beneath the editorial type */}
        <div className="absolute inset-0 bg-[#0E2750]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E2750] via-[#0E2750]/85 to-[#0E2750]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E2750]/95 via-[#0E2750]/40 to-transparent" />
      </div>

      {/* Soft ambient lighting — much subtler than before */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#C9A876]/[0.05] blur-[180px] z-0" />
      <div className="pointer-events-none absolute bottom-0 -left-20 h-[420px] w-[420px] rounded-full bg-[#1d3b6e]/40 blur-[160px] z-0" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-40 md:pt-52 pb-28 md:pb-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="block h-[1px] w-10 bg-[#C9A876]/80" />
          <span className="font-mono-eyebrow text-[#C9A876]">
            Founder-Led Executive Search · United Kingdom
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-display text-5xl sm:text-6xl lg:text-[88px] tracking-tight leading-[1.02] text-white max-w-5xl"
        >
          Building leadership teams for{" "}
          <span className="font-display-italic text-[#C9A876]">ambitious</span> businesses.
        </motion.h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-slate-200/80"
        >
          ArcPoint is a UK-focused, founder-led executive search firm
          partnering with founders, MDs and scaling businesses to secure
          high-impact senior operators across commercial, operational and
          technology leadership.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-14 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-book-call"
            onClick={() => goTo("contact")}
            className="group inline-flex items-center justify-center gap-2 px-8 py-[18px] bg-[#C9A876] hover:bg-[#DCC298] text-[#0E2750] text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors duration-500"
          >
            Book a Confidential Call
            <ArrowUpRight
              size={14}
              strokeWidth={1.8}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </button>
          <button
            data-testid="hero-cta-view-opportunities"
            onClick={() => goTo("opportunities")}
            className="group inline-flex items-center justify-center gap-2 px-8 py-[18px] border border-[#C9A876]/55 text-[#C9A876] hover:bg-[#C9A876]/10 hover:border-[#C9A876] text-[12px] uppercase tracking-[0.22em] font-medium transition-all duration-500"
          >
            Explore Talent Network
            <MoveRight
              size={14}
              strokeWidth={1.5}
              className="transition-transform group-hover:translate-x-1"
            />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-8 max-w-4xl"
        >
          {[
            { v: "Retained", k: "Search Mandates" },
            { v: "C-Suite", k: "Operator Focus" },
            { v: "UK", k: "Founder Network" },
            { v: "Discreet", k: "By Design" },
          ].map((s) => (
            <div key={s.k} className="border-l border-[#C9A876]/40 pl-5">
              <div className="font-display text-[28px] md:text-[34px] text-white tracking-tight leading-none">
                {s.v}
              </div>
              <div className="font-mono-eyebrow text-slate-300/70 mt-2.5">{s.k}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-slate-400"
      >
        <span className="font-mono-eyebrow">Scroll</span>
        <div className="h-12 w-[1px] bg-gradient-to-b from-[#C9A876]/60 to-transparent" />
      </motion.div>
    </section>
  );
}
